'use client';

import type { ReactNode } from 'react';
import { useHasWastageState } from './hooks';

interface WastageSwitchRootProps {
  children: ReactNode;
  className?: string;
}

function WastageSwitchRoot({
  children,
  className = 'flex h-full min-w-[125px] flex-col justify-center gap-1',
}: WastageSwitchRootProps) {
  return <div className={className}>{children}</div>;
}

interface WastageLabelProps {
  children: ReactNode;
  className?: string;
}

function WastageLabel({
  children,
  className = 'font-bold text-sm',
}: WastageLabelProps) {
  return <div className={className}>{children}</div>;
}

interface WastageToggleProps {
  className?: string;
  checkedClassName?: string;
  thumbClassName?: string;
  onToggle?: (checked: boolean) => void;
}

function WastageToggle({
  className = 'relative h-7 w-12 cursor-pointer rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
  checkedClassName = 'data-[state=checked]:bg-primary',
  thumbClassName = 'block size-6 translate-x-0.5 rounded-full bg-white shadow-lg transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[22px]',
  onToggle,
}: WastageToggleProps) {
  const { hasWastage, setHasWastage } = useHasWastageState();

  const handleCheckedChange = (checked: boolean) => {
    if (onToggle) {
      onToggle(checked);
    } else if (checked) {
      setHasWastage(true);
    } else {
      setHasWastage(false);
    }
  };

  return (
    <label className="inline-flex cursor-pointer items-center">
      <input
        type="checkbox"
        checked={hasWastage}
        onChange={(e) => handleCheckedChange(e.target.checked)}
        className="sr-only"
      />
      <div className={`${className} ${hasWastage ? checkedClassName : ''}`}>
        <div className={thumbClassName} />
      </div>
    </label>
  );
}

WastageSwitchRoot.Label = WastageLabel;
WastageSwitchRoot.Toggle = WastageToggle;

export const WastageSwitch = WastageSwitchRoot;
